import React, { useState, useEffect, useContext } from 'react';
import axiosInstance from '../../axiosConfig';
import { AuthContext } from '../../contexts/AuthContext';
import '../../styles/AdminTicketManagement.css';
//import { Circles } from 'react-loader-spinner'; // Importing the loader component
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import ResumeSkeleton from '../Loading/ResumeSkeleton';

const AdminTicketManagement = () => {
    const { auth, users } = useContext(AuthContext);

    const [tickets, setTickets] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [ticketDetails, setTicketDetails] = useState(null);
    const [selectedPriority, setSelectedPriority] = useState('');
    const [selectedAssignee, setSelectedAssignee] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('asc');
    const ticketsPerPage = 10;
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTickets = async () => {
            if (!auth?.isAdmin) {
               // console.log('Token not available. Redirecting to login...');
                navigate('/login');
                return;
            }

            setLoading(true);
            try {
                const response = await axiosInstance.get('/api/admin/support-tickets', {
                    headers: { Authorization: `Bearer ${auth.accessToken}` }
                });
                setTickets(response.data);
                setError(null);
            } catch (error) {
               // console.error('Error fetching tickets:', error);
                setError('Failed to fetch tickets. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchTickets();

    }, [auth, navigate]);

    const handleStatusChange = async (ticketId, newStatus) => {
        try {
            const response = await axiosInstance.put(
                `/api/admin/support-tickets/${ticketId}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
            );
            const updatedTicket = response.data;
            setTickets(tickets.map(ticket => (ticket.id === updatedTicket.id ? updatedTicket : ticket)));
        } catch (error) {
            //console.error('Error updating support ticket status', error);
        }
    };

    const handleAssignTicket = async (ticketId, assignedTo) => {
        try {
            const response = await axiosInstance.put(
                `/api/admin/support-tickets/assign/${ticketId}`,
                { assignedTo },
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
            );
            const updatedTicket = response.data;
            setTickets(tickets.map(ticket => (ticket.id === updatedTicket.id ? updatedTicket : ticket)));
        } catch (error) {
           // console.error('Error assigning support ticket', error);
        }
    };

    const handleBulkStatusChange = async (newStatus) => {
        try {
            await Promise.all(selectedTickets.map(ticketId =>
                axiosInstance.put(
                    `/api/admin/support-tickets/${ticketId}`,
                    { status: newStatus },
                    { headers: { Authorization: `Bearer ${auth.accessToken}` } }
                )
            ));
            setTickets(tickets.map(ticket => selectedTickets.includes(ticket.id) ? { ...ticket, status: newStatus } : ticket));
            setSelectedTickets([]);
        } catch (error) {
           // console.error('Error updating support tickets status', error);
        }
    };

    const handleBulkAssign = async (assignedTo) => {
        try {
            await Promise.all(selectedTickets.map(ticketId =>
                axiosInstance.put(
                    `/api/admin/support-tickets/assign/${ticketId}`,
                    { assignedTo },
                    { headers: { Authorization: `Bearer ${auth.accessToken}` } }
                )
            ));
            setTickets(tickets.map(ticket => selectedTickets.includes(ticket.id) ? { ...ticket, assignedTo } : ticket));
            setSelectedTickets([]);
        } catch (error) {
           // console.error('Error assigning support tickets', error);
        }
    };

    const handleSelectTicket = (ticketId) => {
        setSelectedTickets(prevSelected =>
            prevSelected.includes(ticketId)
                ? prevSelected.filter(id => id !== ticketId)
                : [...prevSelected, ticketId]
        );
    };

    const handleViewTicketDetails = (ticket) => {
        setTicketDetails(ticket);
        setShowModal(true);
    };

    const handleAddComment = async () => {
        try {
            const response = await axiosInstance.post(
                `/api/support/${ticketDetails.id}/comment`,
                { comment: `Support Team reply: ${newComment}` },
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
            );

            const updatedTicket = response.data;
            setTicketDetails(updatedTicket);
            setTickets(tickets.map(ticket => (ticket.id === updatedTicket.id ? updatedTicket : ticket)));
            setNewComment('');
        } catch (error) {
            //console.error('Error adding comment', error);
        }
    };

    const handlePriorityChange = async (ticketId, newPriority) => {
        try {
            const response = await axiosInstance.put(
                `/api/admin/support-tickets/${ticketId}`,
                { priority: newPriority },
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
            );
            const updatedTicket = response.data;
            setTickets(tickets.map(ticket => (ticket.id === updatedTicket.id ? updatedTicket : ticket)));
        } catch (error) {
            console.log("")
           // console.error('Error updating support ticket priority', error);
        }
    };

    const handleSort = (field) => {
        const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(order);
        setTickets([...tickets].sort((a, b) => {
            if (a[field] < b[field]) return sortOrder === 'asc' ? -1 : 1;
            if (a[field] > b[field]) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        }));
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.message.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter ? ticket.status === statusFilter : true;
        const matchesPriority = selectedPriority ? ticket.priority === selectedPriority : true;
        const matchesAssignee = selectedAssignee ? ticket.assignedTo === selectedAssignee : true;
        return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
    });

    const indexOfLastTicket = currentPage * ticketsPerPage;
    const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
    const currentTickets = filteredTickets.slice(indexOfFirstTicket, indexOfLastTicket);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    if (loading)
        return (
            <div className="loader-container" style={{ marginTop: '30vh' }}>
                <ResumeSkeleton/>
            </div>
        )

    if (error) return <p>Error fetching tickets: {error}</p>;


    const isValidDate = (date) => {
        return date instanceof Date && !isNaN(date);
    };


    return (
        <div className="admin-ticket-management-container">
            <div className="admin-ticket-management">
                <h2>Admin Support Ticket Management</h2>
                <div className="search-filter-container">
                    <input
                        type="text"
                        placeholder="Search tickets..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                    </select>
                    <select
                        value={selectedPriority}
                        onChange={e => setSelectedPriority(e.target.value)}
                    >
                        <option value="">All Priorities</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                    </select>
                    <select
                        value={selectedAssignee}
                        onChange={e => setSelectedAssignee(e.target.value)}
                    >
                        <option value="">All Assignees</option>
                        {users && users.map(user => (
                            <option key={user.id} value={user.id}>
                                {user?.email}
                            </option>
                        ))}
                    </select>
                    <button onClick={() => handleBulkStatusChange('open')}>Bulk Open</button>
                    <button onClick={() => handleBulkStatusChange('closed')}>Bulk Close</button>
                    <select
                        onChange={e => handleBulkAssign(e.target.value)}
                    >
                        <option value="">Bulk Assign To</option>
                        {users && users.map(user => (
                            <option key={user.id} value={user.id}>
                                {user?.email}
                            </option>
                        ))}
                    </select>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={e => setSelectedTickets(e.target.checked ? tickets.map(ticket => ticket.id) : [])}
                                    checked={selectedTickets.length === tickets.length}
                                />
                            </th>
                            <th onClick={() => handleSort('subject')}>Subject</th>
                            <th onClick={() => handleSort('createdAt')}>Created At</th>
                            <th onClick={() => handleSort('status')}>Status</th>
                            <th onClick={() => handleSort('priority')}>Priority</th>
                            <th onClick={() => handleSort('assignedTo')}>Assigned To</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentTickets.map(ticket => (
                            <tr key={ticket.id}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedTickets.includes(ticket.id)}
                                        onChange={() => handleSelectTicket(ticket.id)}
                                    />
                                </td>
                                <td>{ticket.subject}</td>
                                <td>
                                    {/* Created At: {format(parseISO(ticket.createdAt), 'PPpp')} */}
                                </td>
                                <td>
                                    <select
                                        value={ticket.status}
                                        onChange={e => handleStatusChange(ticket.id, e.target.value)}
                                    >
                                        <option value="open">Open</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </td>
                                <td>
                                    <select
                                        value={ticket.priority}
                                        onChange={e => handlePriorityChange(ticket.id, e.target.value)}
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                    </select>
                                </td>
                                <td>
                                    <select
                                        value={ticket.assignedTo || ''}
                                        onChange={e => handleAssignTicket(ticket.id, e.target.value)}
                                    >
                                        <option value="">Unassigned</option>
                                        {users && users.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user?.email}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <button onClick={() => handleViewTicketDetails(ticket)}>View Details</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="pagination">
                    {[...Array(Math.ceil(filteredTickets.length / ticketsPerPage)).keys()].map(number => (
                        <button
                            key={number + 1}
                            onClick={() => handlePageChange(number + 1)}
                            className={currentPage === number + 1 ? 'active' : ''}
                        >
                            {number + 1}
                        </button>
                    ))}
                </div>

                {showModal && ticketDetails && (
                    <div className="modal">
                        <div className="modal-content">
                            <span className="close" onClick={() => setShowModal(false)}>&times;</span>
                            <h2>Ticket Details</h2>
                            <p><strong>Subject:</strong> {ticketDetails.subject}</p>
                            <p><strong>Message:</strong> {ticketDetails.message}</p>
                            <p><strong>Status:</strong> {ticketDetails.status}</p>
                            <p><strong>Priority:</strong> {ticketDetails.priority}</p>
                            <p><strong>Assigned To:</strong> {users.find(user => user.id === ticketDetails.assignedTo)?.email || 'Unassigned'}</p>
                            <div className="comments-section">
                                <h3>Comments</h3>
                                {ticketDetails.comments && ticketDetails.comments.map((comment, index) => (
                                    <p key={index}><strong>{comment?.user?.email}:</strong> {comment.comment}</p>
                                ))}
                                <textarea
                                    value={newComment}
                                    onChange={e => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                ></textarea>
                                <button onClick={handleAddComment}>Add Comment</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


export default AdminTicketManagement;